import { createAxiosInstance } from "./ServicesHelpers";
import config from "../config.json";
import qs from "qs";
import { IPageable, Params } from "../types/table/interfaces.type";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.CDR_API_BASE_URL);

export interface CdrExport {
  id: number;
  fileName: string;
  fileSize: number;
  createdDate: string;
  createdBy: string;
  status: "QUEUED" | "RUNNING" | "ERROR" | "DONE";
  count: number;
  errorMessage?: string;
}

export interface InitiateExportResponse {
  id: string;
  timestamp: string;
  status: "RUNNING";
  count: number;
}

export const initiateMessageTrackerExport = async (
  params: Params<any> = {}
): Promise<InitiateExportResponse> => {
  try {
    const response = await axiosInstance.get("/cdrs/csv", {
      params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    if (response.data?.status === "ERROR") {
      throw new Error(
        response.data.message || "Failed to initiate export. Please try again."
      );
    }
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to initiate export. Please try again.";
    throw new Error(errorMessage);
  }
};

export const getActiveExports = async (): Promise<CdrExport[]> => {
  try {
    const response = await axiosInstance.get("/cdr-exports/active");
    return response.data || [];
  } catch (error: any) {
    console.error("Error fetching active exports:", error);
    return [];
  }
};

export const getExportFile = async (
  id: string,
  onDownloadProgress?: (progress: number) => void,
): Promise<{ filename: string; blob: Blob }> => {
  try {
    const response = await axiosInstance.get(`/cdr-exports/${id}/file`, {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onDownloadProgress(progress);
        }
      },
    });

    const disposition = response.headers["content-disposition"];
    let filename = "MessageTracker.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    return {
      filename,
      blob: new Blob([response.data], { type: "text/csv" }),
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      "Failed to download export file. Please try again.";
    throw new Error(errorMessage);
  }
};

export const getAllCdrExports = async (
  params: Params<CdrExport> = {}
): Promise<IPageable<CdrExport>> => {
  try {
    const response = await axiosInstance.get("/cdr-exports", {
      params: { ...params, sort: params.sort || "createdDate,desc" },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch CDR exports."
    );
  }
};

export const deleteCdrExport = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/cdr-exports/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to delete CDR export."
    );
  }
};

export const useCdrExportsQuery = (
  params: Params<CdrExport>
): UseQueryResult<IPageable<CdrExport>, Error> => {
  return useQuery({
    queryKey: ["cdrExports", JSON.stringify(params)],
    queryFn: () => getAllCdrExports(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 3000,
  });
};
